import React from 'react';
import { Link } from 'react-router-dom';
import { Coffee, Heart, Leaf, Award, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';

export default function AboutPage() {
  const pageTitle = "À propos | Léger's Coffee à Genève";
  const pageDescription =
    "Découvrez l'histoire de Léger's Coffee à Genève : notre passion pour le café de spécialité, nos valeurs, notre savoir-faire barista et notre sélection de grains d'exception.";
  const canonicalUrl = 'https://legerscoffee.fr/a-propos';
  const ogImage = 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=1200&q=80';

  const structuredDataAboutPage = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: "À propos | Léger's Coffee",
    url: canonicalUrl,
    description: pageDescription,
    inLanguage: 'fr-CH'
  };

  const structuredDataBusiness = {
    '@context': 'https://schema.org',
    '@type': 'CafeOrCoffeeShop',
    name: "Léger's Coffee",
    url: 'https://legerscoffee.fr/',
    image: [ogImage],
    description: pageDescription,
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Pl. des Augustins 19',
      postalCode: '1205',
      addressLocality: 'Genève',
      addressCountry: 'CH'
    }
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Léger's Coffee" />
        <meta property="og:locale" content="fr_CH" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImage} />

        <script type="application/ld+json">
          {JSON.stringify(structuredDataAboutPage)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(structuredDataBusiness)}
        </script>
      </Helmet>

      <main data-testid="about-page" className="min-h-screen pt-20">
        {/* Hero */}
        <section className="relative bg-espresso text-warm-alabaster py-24 lg:py-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="max-w-3xl">
              <span className="text-latte-gold text-sm uppercase tracking-widest font-medium mb-6 block">
                Notre Histoire
              </span>
              <h1 className="font-serif text-4xl lg:text-5xl font-semibold mb-6">
                La passion du café,<br />
                <span className="text-latte-gold">depuis 2014</span>
              </h1>
              <p className="text-warm-alabaster/80 text-lg leading-relaxed">
                Niché au cœur de Genève, Léger's Coffee est né d'une passion profonde 
                pour le café d'exception et l'art de l'hospitalité.
              </p>
            </div>
          </div>
        </section>

        {/* Story Section */}
        <section className="section-padding bg-warm-alabaster">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <div>
                <img
                  src="https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=800&q=80"
                  alt="Notre café"
                  className="w-full aspect-[4/5] object-cover"
                />
              </div>
              <div>
                <span className="text-latte-gold text-sm uppercase tracking-widest font-medium mb-4 block">
                  Qui Sommes-Nous
                </span>
                <h2 className="font-serif text-3xl lg:text-4xl text-espresso font-semibold mb-6">
                  Une aventure gustative au cœur de Genève
                </h2>
                <div className="space-y-4 text-soft-charcoal-secondary leading-relaxed">
                  <p>
                    Tout a commencé par une simple idée : créer un lieu où chaque tasse de café 
                    raconte une histoire. Un espace où la qualité prime sur la quantité, 
                    où chaque grain est soigneusement sélectionné pour offrir une expérience 
                    gustative exceptionnelle.
                  </p>
                  <p>
                    Place des Augustins, dans le quartier historique de Genève, nous avons ouvert 
                    nos portes avec une mission claire : partager notre amour du café de spécialité 
                    avec une communauté grandissante de passionnés.
                  </p>
                  <p>
                    Aujourd'hui, Léger's Coffee est devenu un rendez-vous incontournable pour 
                    les amateurs de café, les travailleurs nomades et tous ceux qui cherchent 
                    un moment de douceur dans leur quotidien.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="section-padding bg-warm-secondary">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <span className="text-latte-gold text-sm uppercase tracking-widest font-medium mb-4 block">
                Nos Valeurs
              </span>
              <h2 className="font-serif text-3xl lg:text-4xl text-espresso font-semibold">
                Ce qui nous guide
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  icon: Coffee,
                  title: 'Qualité',
                  desc: 'Des grains soigneusement sélectionnés auprès de producteurs engagés.'
                },
                {
                  icon: Heart,
                  title: 'Passion',
                  desc: 'Chaque tasse est préparée avec amour et expertise par nos baristas.'
                },
                {
                  icon: Leaf,
                  title: 'Durabilité',
                  desc: 'Un engagement pour des pratiques respectueuses de l\'environnement.'
                },
                {
                  icon: Award,
                  title: 'Excellence',
                  desc: 'Une quête constante de perfection dans chaque détail.'
                }
              ].map((value, index) => (
                <div
                  key={index}
                  className="bg-warm-alabaster p-8 border border-border-subtle text-center"
                >
                  <div className="w-14 h-14 mx-auto mb-6 rounded-full bg-warm-secondary flex items-center justify-center">
                    <value.icon className="w-6 h-6 text-latte-gold" />
                  </div>
                  <h3 className="font-serif text-lg font-semibold text-espresso mb-3">
                    {value.title}
                  </h3>
                  <p className="text-soft-charcoal-secondary text-sm">
                    {value.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Coffee Selection */}
        <section className="section-padding bg-warm-alabaster">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <div className="order-2 lg:order-1">
                <span className="text-latte-gold text-sm uppercase tracking-widest font-medium mb-4 block">
                  Notre Sélection
                </span>
                <h2 className="font-serif text-3xl lg:text-4xl text-espresso font-semibold mb-6">
                  Des grains d'exception
                </h2>
                <div className="space-y-4 text-soft-charcoal-secondary leading-relaxed mb-8">
                  <p>
                    Nous travaillons exclusivement avec des torréfacteurs artisanaux qui 
                    partagent notre vision de la qualité. Nos grains proviennent de fermes 
                    certifiées en Éthiopie, Colombie, Brésil et Guatemala.
                  </p>
                  <p>
                    Chaque lot est testé et sélectionné pour son profil aromatique unique. 
                    Nous privilégions les cafés de spécialité avec un score supérieur à 80 
                    sur l'échelle SCA.
                  </p>
                  <p>
                    De l'espresso corsé au cold brew rafraîchissant, notre carte offre 
                    une palette de saveurs pour satisfaire tous les palais.
                  </p>
                </div>
                <Button asChild className="btn-secondary">
                  <Link to="/menu">
                    Découvrir Notre Carte
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </div>
              <div className="order-1 lg:order-2">
                <img
                  src="https://images.unsplash.com/photo-1518832168546-8e34741a9dfb?w=800&q=80"
                  alt="Grains de café"
                  className="w-full aspect-square object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Savoir-faire */}
        <section className="section-padding bg-espresso text-warm-alabaster">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <div>
                <img
                  src="https://images.unsplash.com/photo-1561522983-385a76fbb4cb?w=800&q=80"
                  alt="Latte art"
                  className="w-full aspect-square object-cover"
                />
              </div>
              <div>
                <span className="text-latte-gold text-sm uppercase tracking-widest font-medium mb-4 block">
                  Notre Savoir-Faire
                </span>
                <h2 className="font-serif text-3xl lg:text-4xl font-semibold mb-6">
                  L'art du barista
                </h2>
                <div className="space-y-4 text-warm-alabaster/80 leading-relaxed">
                  <p>
                    Nos baristas sont formés aux techniques les plus exigeantes de 
                    l'extraction du café. Du réglage du moulin à la température de l'eau, 
                    chaque paramètre est maîtrisé pour offrir une tasse parfaite.
                  </p>
                  <p>
                    Le latte art est pour nous bien plus qu'une décoration : c'est le 
                    témoignage d'une mousse parfaitement texturée et d'un espresso 
                    idéalement extrait.
                  </p>
                  <p>
                    Nous organisons régulièrement des ateliers de dégustation et des 
                    formations pour partager notre passion avec notre communauté.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section-padding bg-warm-secondary">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-serif text-3xl lg:text-4xl text-espresso font-semibold mb-6">
              Venez nous rendre visite
            </h2>
            <p className="text-soft-charcoal-secondary mb-8 max-w-2xl mx-auto">
              Nous serions ravis de vous accueillir dans notre espace chaleureux. 
              Que ce soit pour un café rapide ou un moment de détente, 
              nos portes vous sont toujours ouvertes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild className="btn-primary">
                <Link to="/contact">Nous Contacter</Link>
              </Button>
              <Button asChild className="btn-secondary">
                <Link to="/reservation">Faire une Réservation</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}